import React, {Fragment, useEffect} from "react";
import {useRouter} from "next/router";
import shopApi from "../../../api/shop";
import {IPageProps} from "../../../types/pages";
import {GetServerSideProps, NextPage} from "next";
import {domainUrl} from "../../../helper";
import PostCategoryPage from "../../../components/posts/PostCategoryPage";
import {IPostCategory} from "../../../types/posts";
import MainLayout from "../../../components/ForCWV/Wrappers/MainLayout";
import {BlogPageTranslations} from "../../../services/translationKeys";
import {layoutProps} from "../../../services/layoutProps";
import Head from "next/head";

interface IProps extends IPageProps {
    postCategory: IPostCategory,
}

const PagePost: NextPage<IProps> = (pageProps) => {
    const {
        domain,
        dbName,
        posts,
        breadcrumb,
        selectedLocale,
        postCategory,
        translationsData,
        siteName,
        defaultLangCode,
        locales
    } = pageProps

    const {
        meta_title = "",
        meta_description = "",
        meta_keywords = "",
        meta_image = ""
    } = postCategory

    const router = useRouter();
    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)
    const meta_img = domainUrl(`${dbName}/storage/${domain}/${meta_image}`)

    useEffect(() => {
        window.onpopstate = () => {
            router.push(window.location.pathname)
        }
    }, [router.query.categorySlug, selectedLocale, router.route])

    return (
        <MainLayout {...pageProps}>
            <Fragment>
                <Head>
                    <title>{meta_title}</title>
                    <meta name="title" content={meta_title}/>
                    <meta name="description" content={meta_description}/>
                    <meta name="keywords" content={meta_keywords}/>
                    <meta name="generator" content="Zegashop.com"/>
                    <meta name="robots" content="index, follow"/>
                    <meta name="twitter:description" content={meta_description}/>
                    <meta name="twitter:title" content={meta_title}/>
                    <meta name="twitter:image" content={meta_img}/>
                    <meta property="og:description" content={meta_description}/>
                    <meta property="og:title" content={meta_title}/>
                    <meta property="og:keywords" content={meta_keywords}/>
                    <meta property="og:url" content={meta_url}/>
                    <meta property="og:locale" content={selectedLocale}/>
                    <meta property="og:image" content={meta_img}/>
                    <meta property="og:site_name" content={siteName}/>
                    <link rel="canonical" href={meta_url}/>
                    {locales?.length > 0 ? (
                        <>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                            {locales.map(intl => (
                                <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                            ))}
                        </>
                    ) : null}
                </Head>
                <PostCategoryPage
                    dbName={dbName}
                    domain={domain}
                    posts={posts}
                    breadcrumb={breadcrumb}
                    postCategory={postCategory}
                    translationsData={translationsData}
                    selectedLocale={selectedLocale}
                />
            </Fragment>
        </MainLayout>
    );
}

export const getServerSideProps: GetServerSideProps = async ({locale, req, query}: any) => {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: BlogPageTranslations,
        ipv4: typedReq.ipv4
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, Posts: PostsIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const dbName = req.headers["x-forwarded-host"]
    const domain = req.defaultDB.db_name
    const {categorySlug: slug, page = 1} = query;
    const selectedCategory = await shopApi.getAsyncPostsCatBySlug({
        locale,
        slug,
        dbName
    }, req.m)
    if (selectedCategory.notFound) {
        return {
            notFound: true
        }
    }
    const postCategory = JSON.parse(JSON.stringify(selectedCategory))

    const options = {
        locale: selectedLocale,
        page: page,
        limit: 18,
        dbName,
        domain,
        filterCategory: selectedCategory.slug
    }
    const posts = await shopApi.getAsyncPosts(options, req.m)

    const postsData = {
        data: posts.data,
        total: posts?.meta?.total || 3,
        current_page: page || posts.meta.current_page,
    };

    const breadcrumb = [
        {title: HomeIntl, url: "/"},
        {title: PostsIntl, url: "/posts"},
        {title: postCategory.name, url: `/${postCategory.slug}`}
    ]

    return {
        props: {
            breadcrumb,
            locale: selectedLocale,
            posts: postsData,
            postCategory,
            ...wrapperProps,
        },
    };
}

export default PagePost